import HexagonLoader from '../../../components/HexagonLoader/HexagonLoader';
import { useState, useEffect, useMemo } from 'react';
import UserAvatarSVG from '../../../components/UserAvatarSVG';
import { useAuth } from '../../../context/AuthContext';
import ArrearsLegalData from '../ExecutiveDash/ArrearsLegalData';
import MonthlyArrearsTable from '../ExecutiveDash/MonthlyArrearsTable';
import '../../../styles/LegalDashStyles.scss';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend, ResponsiveContainer } from 'recharts'
import * as Sentry from '@sentry/react';

const badDebtsList = [
    "Provision for Bad Debts",
    "Bad debts written off",
    "Bad Debts Recovered"
];

const incomeList = [
    "Basic Contractual Income",
    "Rent Turnover Income",
    "Insurance Income - Revenue",
    "Covid Credits - Rental",
    "Other Income",
    "Other Contractual Income",
    "Promotional Income",
];

const billingsList = [
    "Basic Contractual Income",
    "Rent Turnover Income",
    "Other Contractual Income",
    "Promotional Income",
    "Rates Income",
    "Electricity Recovered",
    "Water Recovered",
    "Sewerage Recovered ",
    "Refuse Recovered"
];

const SquareDot = (props) => {
    const { cx, cy, fill = "#F63040", stroke = "#F63040" } = props;
    return (
        <rect
            x={cx - 4}
            y={cy - 4}
            width={8}
            height={8}
            fill={fill}
            stroke={stroke}
            strokeWidth={2}
            rx={0}
            ry={0}
        />
    );
};

const LegalDashboard = () => {
    const { apiFetch, user } = useAuth();
    const [initialLoading, setInitialLoading] = useState(true);
    const [period, setPeriod] = useState(null);
    const [condensedIncomeData, setCondensedIncomeData] = useState(null);
    const [condensedIncomeDataPrev, setCondensedIncomeDataPrev] = useState(null);
    const [topArrears, setTopArrears] = useState({
        Legal: [],
        NonLegal: []
    });
    const [billingData, setBillingData] = useState([]);
    const [prevArrearsData, setPrevArrearsData] = useState({});
    const [paidToSellerAmounts, setPaidToSellerAmounts] = useState({});
    const [tempFicaData, setTempFicaData] = useState([]);

    useEffect(() => {
        const fetchDefaultPeriod = async () => {
            try {
                const response = await apiFetch(`/api/Mda/DefaultPeriod`);
                const data = await response.json();
                setPeriod(data.period1);
            } catch (e) {
                Sentry.captureException(e);
                if (e.message !== 'Token refresh failed') {
                    console.error(e);
                }
            }
        };
        fetchDefaultPeriod();
    }, []);

    const fetchCondensedIncomeStatement = async () => {
        try {
            const response = await apiFetch(`/api/Mda/CondensedIncomeStatementData?Period=${period}`);
            const data = await response.json();
            if (data.success) {
                setCondensedIncomeData(data.data);
            } else {
                console.error('Failed to fetch trial balance data:', data.message);
            }
        } catch (err) {
            Sentry.captureException(err);
            if (err.message !== 'Token refresh failed') {
                console.error('Error fetching trial balance data:', err);
            }
        }
    };

    const fetchPrevCondensedIncomeStatement = async () => {
        try {
            const { prevYear } = getMonthYear(period);
            const prevDecember = `${prevYear}12`;
            const prevDecResponse = await apiFetch(`/api/Mda/CondensedIncomeStatementData?Period=${prevDecember}`);
            const prevDecData = await prevDecResponse.json();
            if (prevDecData.success) {
                setCondensedIncomeDataPrev(prevDecData.data);
            } else {
                console.error('Failed to fetch previous December data:', prevDecData.message);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchTop10LegalOrNonLegalArrears = async () => {
        try {
            const response = await apiFetch(`/api/Mda/TopLegalAndNonLegalArrears?Period=${period}`);
            const data = await response.json();
            if (data.success && data.data) {
                setTopArrears({
                    Legal: data.data.legal ?? [],
                    NonLegal: data.data.nonLegal ?? []
                });
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchArrearsFor12PrevPeriods = async () => {
        try {
            const response = await apiFetch(`/api/Mda/GetArrearsForLast12Periods?period=${period}`);
            const data = await response.json();
            if (data.success) {
                setPrevArrearsData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchBillings = async () => {
        try {
            const response = await apiFetch(`/api/Mda/SumInclusiveAmountByPeriod?period=${period}`);
            const data = await response.json();
            if (data.success) {
                setBillingData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchPaidToSellerAmounts = async () => {
        try {
            const response = await apiFetch(`/api/Leasing/PaidToSeller13MonthRange?period=${period}`);
            const data = await response.json();
            if (data.success) {
                setPaidToSellerAmounts(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    const fetchTempFicaData = async () => {
        try {
            const response = await apiFetch('/api/Ops/TempTenantFicaStats/all');
            if (!response || !response.ok) return;
            const text = await response.text();
            if (!text) return;
            const data = JSON.parse(text);
            if (data && data.success && Array.isArray(data.data)) {
                setTempFicaData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    };

    function getMonthYear(period) {
        if (!period) return { monthName: '', year: '', prevYear: '' };
        const periodStr = period.toString().padStart(6, '0');
        if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '' };
        const year = periodStr.substring(0, 4);
        const prevYear = (parseInt(year, 10) - 1).toString();
        const month = periodStr.substring(4, 6);
        const monthNames = [
            '', 'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const monthIndex = parseInt(month, 10);
        return { monthName: monthNames[monthIndex], year, prevYear };
    }

    const { monthName, year, prevYear } = getMonthYear(period);

    useEffect(() => {
        if (!period) return;
        const fetchData = async () => {
            await Promise.all([
                fetchCondensedIncomeStatement(),
                fetchPrevCondensedIncomeStatement(),
                fetchTop10LegalOrNonLegalArrears(),
                fetchArrearsFor12PrevPeriods(),
                fetchBillings(),
                fetchPaidToSellerAmounts(),
                fetchTempFicaData()
            ]);
        };
        fetchData();
    }, [period]);

    const calculateTotalForCaptions = (data, includedCaptions, valueProperty) => {
        if (!data || !Array.isArray(data) || !includedCaptions || !Array.isArray(includedCaptions) || !valueProperty) {
            return 0;
        }
        return data.reduce((sum, item) => {
            if (includedCaptions.includes(item.Caption)) {
                const value = typeof item[valueProperty] === 'number'
                    ? item[valueProperty]
                    : parseFloat(item[valueProperty] || '0');
                return sum + (value * -1);
            }
            return sum;
        }, 0);
    };

    const badDebtPercentages = useMemo(() => {
        if (!condensedIncomeData) {
            return { percentageCurrent: "0.0%", percentageYTD: "0.0%", percentagePrev: "0.0%" };
        }

        const badDebtsCurrent = calculateTotalForCaptions(condensedIncomeData, badDebtsList, "CurrentMovements");
        const totalIncomeCurrent = calculateTotalForCaptions(condensedIncomeData, incomeList, "CurrentMovements");
        const percentageCurrent = ((-badDebtsCurrent / totalIncomeCurrent) * 100).toLocaleString('en-ZA', {
            minimumFractionDigits: 1, maximumFractionDigits: 1
        }) + '%';

        const badDebtsYTD = calculateTotalForCaptions(condensedIncomeData, badDebtsList, "CurrentBal");
        const totalIncomeYTD = calculateTotalForCaptions(condensedIncomeData, incomeList, "CurrentBal");
        const percentageYTD = ((-badDebtsYTD / totalIncomeYTD) * 100).toLocaleString('en-ZA', {
            minimumFractionDigits: 1, maximumFractionDigits: 1
        }) + '%';

        const badDebtsPrev = calculateTotalForCaptions(condensedIncomeDataPrev, badDebtsList, "BudgetFullYearRevised");
        const totalIncomePrev = calculateTotalForCaptions(condensedIncomeDataPrev, incomeList, "BudgetFullYearRevised");
        const percentagePrev = ((-badDebtsPrev / totalIncomePrev) * 100).toLocaleString('en-ZA', {
            minimumFractionDigits: 1, maximumFractionDigits: 1
        }) + '%';

        return { percentageCurrent, percentageYTD, percentagePrev };
    }, [condensedIncomeData, condensedIncomeDataPrev]);

    const billings = useMemo(() => {
        if (!condensedIncomeData || !period) {
            return { monthlyBillingsCurrent: 0, monthlyBillingsYTD: 0, monthlyBillingsPrev: 0 };
        }

        const periodStr = period.toString().padStart(6, '0');
        let monthNum = parseInt(periodStr.slice(-2), 10);
        if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) monthNum = 1;

        const monthlyBillingsCurrent = calculateTotalForCaptions(condensedIncomeData, billingsList, "CurrentMovements");
        const monthlyBillingsYTD = (
            calculateTotalForCaptions(condensedIncomeData, ["Basic Contractual Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeData, ["Rent Turnover Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeData, ["Other Contractual Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeData, ["Promotional Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeData, ["Rates Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeData, ["Electricity Recovered"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeData, ["Water Recovered"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeData, ["Sewerage Recovered "], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeData, ["Refuse Recovered"], "CurrentBal")
        ) / monthNum;
        const monthlyBillingsPrev = (
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Basic Contractual Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Rent Turnover Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Other Contractual Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Promotional Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Rates Income"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Electricity Recovered"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Water Recovered"], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Sewerage Recovered "], "CurrentBal") +
            calculateTotalForCaptions(condensedIncomeDataPrev, ["Refuse Recovered"], "CurrentBal")
        ) / 12;

        return { monthlyBillingsCurrent, monthlyBillingsYTD, monthlyBillingsPrev };
    }, [condensedIncomeData, condensedIncomeDataPrev, period]);

    function formatNumberWithSpaces(num) {
        return Math.round(num)
            .toLocaleString('fr-FR')
            .replace(/[\u202F\u00A0 ]/g, ' ');
    }

    const renderArrearsTable = (arrearsList, title) => {
        const totalDue = arrearsList.reduce((sum, item) => sum + Math.round(item.taxExclusiveAmount), 0);
        const totalProvision = totalDue;

        return (
            <div className="arrears-table-container">
                <table className="arrears-table">
                    <thead>
                        <tr>
                            <th>{title}</th>
                            <th>Tenant</th>
                            <th>Total Due<br />{monthName} {year}</th>
                            <th>Provision<br />{monthName} {year}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {arrearsList.map((item, idx) => (
                            <tr key={idx}>
                                <td>{item.propertyName ?? ''}</td>
                                <td>{item.tenantName}</td>
                                <td>{formatNumberWithSpaces(item.taxExclusiveAmount)}</td>
                                <td>{formatNumberWithSpaces(item.taxExclusiveAmount)}</td>
                            </tr>
                        ))}
                        <tr className="arrears-table-total-row">
                            <td><strong>Total</strong></td>
                            <td></td>
                            <td><strong><span className="total-due-align">{formatNumberWithSpaces(totalDue)}</span></strong></td>
                            <td><strong>{formatNumberWithSpaces(totalProvision)}</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    useEffect(() => {
        const timer = setTimeout(() => setInitialLoading(false), 3000);
        return () => clearTimeout(timer);
    }, []);

    function labelToPeriod(label) {
        const [monthStr, yearStr] = label.split(' ');
        const monthMap = {
            Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
            Jul: '07', Aug: '08', Sep: '09', Sept: '09', Oct: '10', Nov: '11', Dec: '12'
        };
        return Number(yearStr + monthMap[monthStr]);
    }

    const monthLabels = Object.keys(prevArrearsData);
    const graphData = monthLabels.map(label => {
        const p = labelToPeriod(label);
        const sortedBillings = [...billingData].sort((a, b) => a.period - b.period);
        const idx = sortedBillings.findIndex(b => b.period === p);
        const slice = sortedBillings.slice(Math.max(0, idx - 11), idx + 1);
        const billingsSumInclusive = slice.reduce((sum, item) => sum + (item.totalInclusiveAmount || 0), 0);
        const billingsSumExclusive = billingsSumInclusive / 1.15;
        const paidToSeller = paidToSellerAmounts?.[p] ?? 0;
        const adjustedArrears = (prevArrearsData[label] || 0) - paidToSeller;
        const percent = billingsSumExclusive !== 0 ? (adjustedArrears / billingsSumExclusive) * 100 : 0;
        return { x: label, y: percent };
    });
    const averagePercent = graphData.length
        ? graphData.reduce((sum, d) => sum + d.y, 0) / graphData.length
        : 0;

    const renderFicaTable = () => {
        const periodStr = period ? period.toString().padStart(6, '0') : '';
        const currentPeriod = periodStr ? Number(periodStr) : null;
        const prevYearPeriod = periodStr
            ? Number((parseInt(periodStr.slice(0, 4), 10) - 1).toString() + periodStr.slice(4, 6))
            : null;

        const currentData = tempFicaData.find(d => d.period === currentPeriod) || {};
        const prevData = tempFicaData.find(d => d.period === prevYearPeriod) || {};
        const getValue = (obj, key) => obj && typeof obj[key] === 'number' ? obj[key] : 0;

        const rows = [
            { label: "Total Tenants in portfolio", current: getValue(currentData, "totalTenants"), prev: getValue(prevData, "totalTenants") },
            { label: "Fica compliant", current: getValue(currentData, "ficaCompliant"), prev: getValue(prevData, "ficaCompliant") },
            { label: "Fica non compliant", current: getValue(currentData, "ficaNonCompliant"), prev: getValue(prevData, "ficaNonCompliant") }
        ];

        return (
            <div className="fica-table-container">
                <table className="fica-table">
                    <thead>
                        <tr>
                            <th>FICA Statistics</th>
                            <th>{periodStr ? `${monthName} ${year}` : "Current Period"}</th>
                            <th>{periodStr ? `${monthName} ${prevYear}` : "Prev Year Same Month"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <tr key={idx}>
                                <td>{row.label}</td>
                                <td>{row.current}</td>
                                <td>{row.prev}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <>
            {initialLoading ? (
                <HexagonLoader />
            ) : (
                <div className="legal-main-container">
                    <div className="legal-header">
                        <h1 className="dashboard-greeting">
                            <UserAvatarSVG />
                            Hello {user.FirstName || user.firstName}
                        </h1>
                        <h3>Legal and Arrears Dashboard</h3>
                    </div>
                    <div className="legal-container-row">
                        <div className="legal-top-row">
                            <ArrearsLegalData period={period} badDebtPercentages={badDebtPercentages} billings={billings} hideLegalRows={false} />
                        </div>
                        <div className="legal-top-row">
                            {renderFicaTable()}
                        </div>
                    </div>
                    <div className="legal-container-row">
                        <div className="legal-middle-row table-spacing">
                            <MonthlyArrearsTable period={period} />
                        </div>
                        <div className="legal-middle-row">
                            <div className="legal-graph-card">
                                <div className="graph-heading-arrears-new">
                                    Normalised Arrears as % of Billings Graph
                                </div>
                                <div className="graph-data">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={graphData}>
                                            <CartesianGrid stroke="#000" vertical={false} fill="#f5f5f5" />
                                            <XAxis
                                                dataKey="x"
                                                dy={2}
                                                tick={{ fontSize: 12 }}
                                                tickFormatter={(label) => {
                                                    const parts = label.split(' ');
                                                    return parts.length === 2
                                                        ? `${parts[0]} ${parts[1].slice(-2)}`
                                                        : label;
                                                }}
                                            />
                                            <YAxis
                                                domain={[1.2, 2.4]}
                                                ticks={[1.2, 1.4, 1.6, 1.8, 2.0, 2.2, 2.4]}
                                            />
                                            <Tooltip formatter={value => (Math.round(value * 10) / 10).toFixed(1)} />
                                            <Legend
                                                verticalAlign="bottom"
                                                align="center"
                                                wrapperStyle={{ paddingTop: 10, paddingLeft: 30 }}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="y"
                                                stroke="#f63040"
                                                strokeWidth={2}
                                                dot={<SquareDot fill="#f63040" stroke="#f63040" />}
                                                name="Normalised arrears as percentage of billings ex VAT"
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="Linear"
                                                stroke="#57565b"
                                                strokeWidth={2}
                                                dot={false}
                                                isAnimationActive={false}
                                                name="Linear (Normalised arrears as percentage of billings ex VAT)"
                                            />
                                            <ReferenceLine y={averagePercent} stroke="#57565b" strokeDasharray="3 3" strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="legal-container-row">
                        <div className="legal-bottom-row left-side">
                            {renderArrearsTable(topArrears.Legal, "Top 10 Legal Arrears")}
                        </div>
                        <div className="legal-bottom-row">
                            {renderArrearsTable(topArrears.NonLegal, "Top 10 Non Legal Arrears")}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LegalDashboard;